import { useState, useCallback, useEffect, RefObject } from 'react';
import type { Cell, Rect } from '../types';

// Re-export types for convenience
export type { Cell, Rect } from '../types';

// Helper to normalize a selection range
export const normalizeSelection = (start: Cell, end: Cell, colOrder: string[]): Rect => {
  const c0 = Math.min(colOrder.indexOf(start.colKey), colOrder.indexOf(end.colKey));
  const c1 = Math.max(colOrder.indexOf(start.colKey), colOrder.indexOf(end.colKey));
  const r0 = Math.min(start.row, end.row);
  const r1 = Math.max(start.row, end.row);
  return { r0, c0, r1, c1, colOrder };
};

interface UseSelectionProps {
  colOrder: string[];
  wrapperRef: RefObject<HTMLDivElement>;
  isEditing: boolean;
}

export function useSelection({ colOrder, wrapperRef, isEditing }: UseSelectionProps) {
  const [activeCell, setActiveCell] = useState<Cell | null>(null);
  const [anchorCell, setAnchorCell] = useState<Cell | null>(null);
  const [selectionRect, setSelectionRect] = useState<Rect | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);

  const onCellClick = useCallback((row: number, colKey: string) => {
    const cell = { row, colKey };
    setActiveCell(cell);
    setAnchorCell(cell);
    const rect = normalizeSelection(cell, cell, colOrder);
    setSelectionRect(rect);

    // Ensure the wrapper gets focus so keyboard shortcuts work
    setTimeout(() => {
      if (wrapperRef.current) {
        wrapperRef.current.focus();
      }
    }, 0);
  }, [colOrder, wrapperRef]);

  const onCellMouseDown = useCallback((row: number, colKey: string) => {
    const cell = { row, colKey };
    setActiveCell(cell);
    setAnchorCell(cell);
    setIsSelecting(true);
    const rect = normalizeSelection(cell, cell, colOrder);
    setSelectionRect(rect);

    // Kill any existing browser selection
    const sel = window.getSelection?.();
    sel?.removeAllRanges();
  }, [colOrder]);

  const onCellMouseMove = useCallback((row: number, colKey: string) => {
    if (isSelecting && activeCell) {
      const cell = { row, colKey };
      const rect = normalizeSelection(activeCell, cell, colOrder);
      setSelectionRect(rect);
    }
  }, [isSelecting, activeCell, colOrder]);

  const clearSelection = useCallback(() => {
    setActiveCell(null);
    setSelectionRect(null);
    setAnchorCell(null);
  }, []);

  const expandSelection = useCallback((newCell: Cell) => {
    const anchor = anchorCell || activeCell;
    if (anchor) {
      const rect = normalizeSelection(anchor, newCell, colOrder);
      setSelectionRect(rect);
      setActiveCell(newCell);
      if (!anchorCell) setAnchorCell(activeCell);
    }
  }, [anchorCell, activeCell, colOrder]);

  const moveActiveCell = useCallback((newCell: Cell) => {
    setActiveCell(newCell);
    setAnchorCell(newCell);
    setSelectionRect(normalizeSelection(newCell, newCell, colOrder));
  }, [colOrder]);

  // Global mouseup to stop selection
  useEffect(() => {
    const handleMouseUp = () => setIsSelecting(false);
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, []);

  // Clear selection when clicking outside the table
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Don't clear if we're clicking on an input, textarea, or button
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'BUTTON') {
        return;
      }

      // Check if click is outside the table wrapper
      if (wrapperRef.current && !wrapperRef.current.contains(target)) {
        clearSelection();
      } else if (wrapperRef.current && wrapperRef.current.contains(target)) {
        // Click is inside the table wrapper - check if it's on a cell
        const cell = target.closest('td, th');
        if (!cell) {
          clearSelection();
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [wrapperRef, clearSelection]);

  // Toggle is-selecting class on wrapper
  useEffect(() => {
    if (!wrapperRef.current) return;
    wrapperRef.current.classList.toggle('is-selecting', isSelecting);
  }, [isSelecting, wrapperRef]);

  // Ensure wrapper is focused when activeCell changes
  useEffect(() => {
    if (activeCell && wrapperRef.current && !isEditing) {
      setTimeout(() => {
        if (wrapperRef.current &&
            document.activeElement?.tagName !== 'INPUT' &&
            document.activeElement?.tagName !== 'TEXTAREA') {
          wrapperRef.current.focus();
        }
      }, 0);
    }
  }, [activeCell, isEditing, wrapperRef]);

  return {
    activeCell,
    setActiveCell,
    anchorCell,
    setAnchorCell,
    selectionRect,
    setSelectionRect,
    isSelecting,
    setIsSelecting,
    onCellClick,
    onCellMouseDown,
    onCellMouseMove,
    clearSelection,
    expandSelection,
    moveActiveCell,
  };
}
